import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./Auth";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service icons as simple components
const HandymanIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M21.71 20.29L18 16.61A9 9 0 1 0 16.61 18l3.68 3.68a1 1 0 0 0 1.42 0 1 1 0 0 0 0-1.39zM11 18a7 7 0 1 1 7-7 7 7 0 0 1-7 7z"/>
    <path d="M13.5 8L11 10.5 8.5 8 7 9.5l2.5 2.5L7 14.5 8.5 16l2.5-2.5 2.5 2.5 1.5-1.5-2.5-2.5 2.5-2.5z"/>
  </svg>
);

const PlumbingIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 8h-1V6c0-1.1-.9-2-2-2H8C6.9 4 6 4.9 6 6v2H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM8 6h8v2H8V6zm11 14H5V10h14v10z"/>
    <path d="M12 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
  </svg>
);

const ElectricalIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/>
  </svg>
);

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="2"/>
    <circle cx="12" cy="12" r="2"/>
    <circle cx="19" cy="12" r="2"/>
  </svg>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [serviceCategory, setServiceCategory] = useState("");
  const [serviceDescription, setServiceDescription] = useState("");
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isEmergency, setIsEmergency] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState({ base: 100, multiplier: 1.0, total: 100 });
  const [showAllServices, setShowAllServices] = useState(false);

  const mainServices = [
    { name: "Handyman", icon: HandymanIcon },
    { name: "Plumbing", icon: PlumbingIcon },
    { name: "Electrical", icon: ElectricalIcon },
  ];

  const allServices = [
    { name: "Handyman", icon: "🔨" },
    { name: "Plumbing", icon: "🔧" },
    { name: "Electrical", icon: "⚡" },
    { name: "HVAC", icon: "❄️" },
    { name: "Cleaning", icon: "🧹" },
    { name: "Moving", icon: "📦" },
    { name: "Landscaping", icon: "🌿" }
  ];

  // D-ID ROMAN agent - loads when on step 1 (home screen)
  useEffect(() => {
    // Only load D-ID on step 1 where the avatar is shown
    if (step !== 1) return;
    
    let scriptElement = null;
    
    const cleanupDID = () => {
      // Remove any existing D-ID scripts
      document.querySelectorAll('script[src*="d-id.com"]').forEach(s => s.remove());
      document.querySelectorAll('script[data-agent-id]').forEach(s => s.remove());
      
      // Remove any D-ID rendered elements (but not our target div)
      document.querySelectorAll('[class*="d-id-"]').forEach(el => el.remove());
      document.querySelectorAll('.did-agent').forEach(el => el.remove());
      
      // Clear the target div content
      const targetDiv = document.getElementById('mobile-did-agent');
      if (targetDiv) {
        targetDiv.innerHTML = '';
      }
    };
    
    const loadDIDAgent = () => {
      // First cleanup any existing D-ID instances
      cleanupDID();
      
      // Create and add new script
      scriptElement = document.createElement('script');
      scriptElement.type = 'module';
      scriptElement.src = 'https://agent.d-id.com/v2/index.js';
      scriptElement.setAttribute('data-mode', 'full');
      scriptElement.setAttribute('data-client-key', 'Z29vZ2xlLW9hdXRoMnwxMTM0MDI1Nzg1OTM5NTA5MTQ3OTU6b0dOeW5WYnJfb0drTU1DVDRoMWJ1');
      scriptElement.setAttribute('data-agent-id', 'v2_agt_rpkLG4Tc');
      scriptElement.setAttribute('data-name', 'did-agent');
      scriptElement.setAttribute('data-monitor', 'true');
      scriptElement.setAttribute('data-target-id', 'mobile-did-agent');
      document.body.appendChild(scriptElement);
    };

    // Load D-ID after a short delay to ensure DOM is ready
    const timer = setTimeout(loadDIDAgent, 500);

    return () => {
      clearTimeout(timer);
      cleanupDID();
    };
  }, [step]); // Re-run when step changes

  const handleServiceSelect = async (category) => {
    setServiceCategory(category);
    setLoading(true);
    setShowAllServices(false);
    
    try {
      const response = await axios.get(`${API}/providers/available?service_type=${category}`);
      setProviders(response.data);
      setStep(2);
    } catch (error) {
      console.error("Failed to fetch providers:", error);
      setProviders([]);
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setIsEmergency(false);
    calculateQuote(false);
    setStep(3);
  };

  const handleEmergencySelect = () => {
    setSelectedProvider(null);
    setIsEmergency(true);
    calculateQuote(true);
    setStep(3);
  };

  const calculateQuote = (emergency) => {
    const baseRate = 100;
    const multiplier = emergency ? 1.5 : 1.0;
    const total = baseRate * multiplier;
    setQuote({ base: baseRate, multiplier, total });
  };

  const handlePayment = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }

    setLoading(true);
    try {
      const paymentData = {
        service_category: serviceCategory,
        description: serviceDescription || `${serviceCategory} service`,
        urgency: isEmergency ? "emergency" : "standard",
        selected_provider_id: selectedProvider?.id || null,
        amount: quote.total
      };

      const response = await axios.post(`${API}/payments/create-checkout`, paymentData);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Payment failed:", error);
      alert("Payment initialization failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    setShowAllServices(true);
  };

  // ============================================
  // STEP 1: HOME SCREEN
  // ============================================
  if (step === 1) {
    return (
      <div className="mobile-home">
        {/* Header with Logo */}
        <div className="home-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_proservice-14/artifacts/c1a3714c_assistr_logo.png" 
            alt="ASSISTR" 
            className="header-logo"
          />
        </div>

        {/* Avatar Section - ROMAN */}
        <div className="avatar-section">
          <div className="avatar-container">
            {/* D-ID will render ROMAN here - div must be empty */}
            <div id="mobile-did-agent" style={{ width: '100%', height: '100%', minHeight: '300px' }}></div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="bottom-section">
          {/* Speech Bubble */}
          <div className="speech-bubble">
            What can we help you with?
          </div>

          {/* Text Input for describing service */}
          <div className="search-input-container">
            <input
              type="text"
              className="search-input"
              placeholder="Describe what you need help with..."
              value={serviceDescription}
              onChange={(e) => setServiceDescription(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && serviceDescription.trim()) {
                  handleGetStarted();
                }
              }}
            />
          </div>

          {/* Service Grid */}
          <div className="service-grid">
            {mainServices.map((service) => (
              <button
                key={service.name}
                className="service-btn"
                onClick={() => handleServiceSelect(service.name)}
                data-testid={`service-${service.name.toLowerCase()}`}
              >
                <span className="service-icon">
                  <service.icon />
                </span>
                <span className="service-name">{service.name}</span>
              </button>
            ))}
            <button
              className="service-btn"
              onClick={handleGetStarted}
              data-testid="service-more"
            >
              <span className="service-icon">
                <MoreIcon />
              </span>
              <span className="service-name">More</span>
            </button>
          </div>

          {/* Get Started Button */}
          <button 
            className="cta-button"
            onClick={handleGetStarted}
            data-testid="get-started-btn"
          >
            Get started
          </button>
        </div>
        {/* All Services Modal */}
        {showAllServices && (
          <div className="modal-overlay" onClick={() => setShowAllServices(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Select a Service</h3>
                <button className="modal-close" onClick={() => setShowAllServices(false)}>×</button>
              </div>
              <div className="providers-list">
                {allServices.map((service) => (
                  <div
                    key={service.name}
                    className="provider-card"
                    onClick={() => handleServiceSelect(service.name)}
                  >
                    <div className="provider-avatar-placeholder" style={{ fontSize: '32px', background: 'var(--panel2)' }}>
                      {service.icon}
                    </div>
                    <div className="provider-info">
                      <div className="provider-name">{service.name}</div>
                      <div className="provider-bio">Professional {service.name.toLowerCase()} services</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // STEP 2: PROVIDER SELECTION
  // ============================================
  if (step === 2) {
    return (
      <div className="mobile-screen">
        <div className="screen-header">
          <button className="back-btn" onClick={() => setStep(1)}>
            ← Back
          </button>
        </div>

        <h1 className="screen-title">Select Your Pro</h1>
        <p className="screen-subtitle">{serviceCategory} Service</p>

        {loading ? (
          <div className="loading-screen">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {/* Provider Cards */}
            <div className="providers-list">
              {providers.length > 0 ? (
                providers.map((provider, index) => (
                  <div
                    key={provider.id}
                    className="provider-card"
                    onClick={() => handleProviderSelect(provider)}
                    data-testid={`provider-option-${index + 1}`}
                  >
                    {provider.provider_photo ? (
                      <img 
                        src={provider.provider_photo} 
                        alt={provider.full_name}
                        className="provider-avatar"
                      />
                    ) : (
                      <div className="provider-avatar-placeholder">
                        {provider.full_name?.charAt(0) || "P"}
                      </div>
                    )}
                    <div className="provider-info">
                      <div className="provider-name">{provider.full_name}</div>
                      <div className="provider-rating">
                        ⭐ {provider.rating?.toFixed(1) || "5.0"} ({provider.completed_jobs || 0} jobs)
                      </div>
                      <div className="provider-bio">
                        {provider.provider_bio || "Experienced professional ready to help"}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">👷</div>
                  <div className="empty-state-text">No providers available right now</div>
                </div>
              )}
            </div>

            {/* Emergency Option */}
            <div
              className="emergency-card"
              onClick={handleEmergencySelect}
              data-testid="emergency-option"
            >
              <div className="emergency-badge">+50% RATE</div>
              <div className="emergency-icon">🚨</div>
              <div className="emergency-title">Emergency Dispatch</div>
              <div className="emergency-desc">
                Get help immediately from any available pro
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ============================================
  // STEP 3: PAYMENT
  // ============================================
  if (step === 3) {
    return (
      <div className="mobile-screen">
        <div className="screen-header">
          <button className="back-btn" onClick={() => setStep(2)}>
            ← Back
          </button>
        </div>

        <h1 className="screen-title">Review & Pay</h1>

        {/* Service Details */}
        <div className="payment-card">
          <div className="payment-row">
            <div>
              <div className="payment-label">Service</div>
              <div className="payment-value">{serviceCategory}</div>
            </div>
          </div>
          <div className="payment-row">
            <div>
              <div className="payment-label">Provider</div>
              <div className="payment-value">
                {isEmergency ? "🚨 Emergency Dispatch" : selectedProvider?.full_name}
              </div>
            </div>
          </div>
        </div>

        {/* Quote Card */}
        <div className="payment-total-card">
          <div className="payment-fee-row">
            <span>Base Rate</span>
            <span>${quote.base.toFixed(2)}</span>
          </div>
          
          {isEmergency && (
            <div className="payment-fee-row emergency-fee">
              <span>Emergency Fee (+50%)</span>
              <span>+${(quote.base * 0.5).toFixed(2)}</span>
            </div>
          )}

          <div className="payment-total-row">
            <span className="payment-total-label">Total</span>
            <span className="payment-total-value">${quote.total.toFixed(2)}</span>
          </div>
        </div>

        {/* Pay Button */}
        <button
          className="cta-button"
          onClick={handlePayment}
          disabled={loading}
          data-testid="pay-now-button"
        >
          {loading ? "Processing..." : user ? "Pay Now with Stripe" : "Sign In to Pay"}
        </button>

        <p className="payment-note">
          Secure payment powered by Stripe
        </p>
      </div>
    );
  }

  return null;
};

export default Dashboard;
